import React, { useEffect } from 'react';
import { Alert, Box, CircularProgress, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useNavigate, useParams } from 'react-router-dom';

import { useGetAllContractsQuery } from '@/apis/extended/agentApi';

const AgentSmartRouter: React.FC = () => {
  const theme = useTheme();
  const { agentId } = useParams<{ agentId: string }>();
  const navigate = useNavigate();

  const {
    data: contracts,
    isLoading,
    error,
  } = useGetAllContractsQuery(agentId ?? '', {
    skip: !agentId,
  });

  useEffect(() => {
    if (!isLoading && contracts && agentId) {
      // Filter contracts that are in negotiation or offer status
      const activeContracts = contracts.filter((contract: any) => {
        const status = contract.contractStatus;
        return (
          status === 'negotiating' || status === 'draft' || status === 'active'
        );
      });

      if (activeContracts.length === 0) {
        // No active contracts, redirect to qualified contract page
        navigate(`/agent/qualified-contract/${agentId}`);
      } else if (activeContracts.length === 1) {
        // Single active contract, redirect directly to property dashboard
        const contractId = activeContracts[0]._id;
        navigate(`/agent/property-dashboard/${contractId}`);
      } else {
        // Multiple active contracts, show selection dashboard
        navigate(`/agent/dashboard/${agentId}`);
      }
    }
  }, [contracts, isLoading, agentId, navigate]);

  if (isLoading) {
    return (
      <Box
        display="flex"
        flexDirection="column"
        justifyContent="center"
        alignItems="center"
        minHeight="60vh"
        gap={2}
      >
        <CircularProgress sx={{ color: theme.palette.mainColors.primary }} />
        <Typography sx={{ color: theme.palette.greyShades.grey1 }}>
          Loading your contracts...
        </Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 4 }}>
        <Alert severity="error">
          Error loading contracts. Please try again.
        </Alert>
      </Box>
    );
  }

  return (
    <Box
      display="flex"
      flexDirection="column"
      justifyContent="center"
      alignItems="center"
      minHeight="60vh"
    >
      <CircularProgress sx={{ color: theme.palette.mainColors.primary }} />
      <Typography sx={{ color: theme.palette.greyShades.grey1, mt: 2 }}>
        Redirecting...
      </Typography>
    </Box>
  );
};

export default AgentSmartRouter;
